import { Alert } from '@ordo/ui/alert'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { AlertOctagon, Check } from 'lucide-react'
import { useState } from 'react'

import { CHIP_CLASS, CHIP_CLASS_PRIMARY, ChipButton } from '@/components/chefs/chip-button'
import type { Campus } from '@/lib/chefs/types'
import { useRemindersStore } from '@/stores/chefs/reminders'

interface MissedDeadlineBannerProps {
  campus: Campus
  deadlineLabel: string
  daysOverdue: number
  className?: string
}

export function MissedDeadlineBanner({ campus, deadlineLabel, daysOverdue, className }: MissedDeadlineBannerProps) {
  const [open, setOpen] = useState(false)
  const reminded = useRemindersStore((s) => s.sent.has(campus.id))
  const markReminded = useRemindersStore((s) => s.markReminded)

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation()
    markReminded(campus.id)
    setOpen(false)
  }

  const overdueLabel = `${daysOverdue} day${daysOverdue === 1 ? '' : 's'} overdue`

  return (
    <>
      <Alert variant="error" className={className}>
        <div className="flex items-start gap-3">
          <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="font-medium">Orders missed deadline</div>
            <div className="text-red-900/80">
              Due {deadlineLabel} — {overdueLabel}, no submission yet.
            </div>
          </div>
          {reminded ? (
            <span className="flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
              <Check className="h-4 w-4" strokeWidth={2} />
              Reminder sent
            </span>
          ) : (
            <ChipButton
              className={`${CHIP_CLASS} shrink-0`}
              onClick={(e) => {
                e.stopPropagation()
                setOpen(true)
              }}>
              Remind school
            </ChipButton>
          )}
        </div>
      </Alert>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Remind {campus.name}?</DialogTitle>
            <DialogDescription>
              Send a reminder to {campus.info.pocName} at {campus.info.phone} asking them to submit this week&rsquo;s
              orders.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <ChipButton className={CHIP_CLASS} onClick={() => setOpen(false)}>
              Cancel
            </ChipButton>
            <ChipButton variant="default" className={CHIP_CLASS_PRIMARY} onClick={handleConfirm}>
              Send reminder
            </ChipButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
